import { useLayoutEffect, useRef, useState } from 'react';
import { KitButton } from './button';
import {
  calendarMonth,
  dateAvailable,
  dayState,
  formatCalendarDate,
  keyboardDate,
  monthDays,
} from './calendar-date.mjs';

export interface CalendarProps {
  id: string;
  locale: string;
  /** From the viewer locale's week info; 0 is Sunday. */
  firstWeekday: number;
  month: string;
  onMonthChange(month: string): void;
  value?: string | null;
  range?: { start: string | null; end: string | null };
  today: string;
  min?: string;
  max?: string;
  disabled?: boolean;
  readOnly?: boolean;
  onSelect(date: string): void;
  copy: { previous: string; next: string };
}

function CalendarHeading({ month, locale, copy, onMonthChange, disabled }: CalendarProps) {
  return (
    <header>
      <KitButton
        variant="quiet"
        disabled={disabled}
        aria-label={copy.previous}
        onClick={() => onMonthChange(calendarMonth(month, -1))}
      >
        ‹
      </KitButton>
      <h3>{formatCalendarDate(`${month}-01`, locale, { day: undefined })}</h3>
      <KitButton
        variant="quiet"
        disabled={disabled}
        aria-label={copy.next}
        onClick={() => onMonthChange(calendarMonth(month, 1))}
      >
        ›
      </KitButton>
    </header>
  );
}

export function Calendar(props: CalendarProps) {
  const { id, month, locale, firstWeekday, today, value, range, onMonthChange } = props;
  const days = monthDays(month, firstWeekday);
  const [active, setActive] = useState(value ?? range?.start ?? today);
  const table = useRef<HTMLTableElement>(null);
  const restoreFocus = useRef(false);
  useLayoutEffect(() => {
    if (!restoreFocus.current) return;
    table.current?.querySelector<HTMLButtonElement>(`[data-date="${active}"]`)?.focus();
    restoreFocus.current = false;
  }, [active, month]);
  const move = (key: string, date: string) => {
    const next = keyboardDate(key, date, firstWeekday);
    if (!next) return false;
    if (!dateAvailable(next, props)) return true;
    setActive(next);
    restoreFocus.current = true;
    if (!next.startsWith(month)) onMonthChange(next.slice(0, 7));
    return true;
  };
  const focused =
    days.includes(active) && dateAvailable(active, props)
      ? active
      : (days.find((date) => date.startsWith(month) && dateAvailable(date, props)) ??
        days.find((date) => dateAvailable(date, props)));
  return (
    <div
      className="pc-date-pop"
      data-state={props.disabled ? 'disabled' : props.readOnly ? 'readonly' : undefined}
    >
      <CalendarHeading {...props} />
      <table
        ref={table}
        id={id}
        className="pc-cal"
        role="grid"
        aria-label={formatCalendarDate(`${month}-01`, locale, { day: undefined })}
      >
        <thead>
          <tr>
            {days.slice(0, 7).map((date) => (
              <th key={date} scope="col">
                {formatCalendarDate(date, locale, {
                  year: undefined,
                  month: undefined,
                  day: undefined,
                  weekday: 'short',
                })}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 6 }, (_, week) => (
            <tr key={week}>
              {days.slice(week * 7, week * 7 + 7).map((date) => (
                <td
                  key={date}
                  role="gridcell"
                  aria-selected={
                    date === value ||
                    date === range?.start ||
                    date === range?.end ||
                    Boolean(range?.start && range?.end && date > range.start && date < range.end)
                  }
                >
                  <button
                    type="button"
                    className="pc-cal-day"
                    data-date={date}
                    data-state={dayState(date, props)}
                    tabIndex={date === focused ? 0 : -1}
                    disabled={!dateAvailable(date, props)}
                    aria-label={formatCalendarDate(date, locale, {
                      dateStyle: 'full',
                      year: undefined,
                      month: undefined,
                      day: undefined,
                    })}
                    aria-current={date === today ? 'date' : undefined}
                    aria-readonly={props.readOnly}
                    onFocus={() => setActive(date)}
                    onClick={() => {
                      if (!props.readOnly) props.onSelect(date);
                    }}
                    onKeyDown={(event) => {
                      if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) {
                        event.stopPropagation();
                        return;
                      }
                      if (move(event.key, date)) event.preventDefault();
                    }}
                  >
                    {new Intl.NumberFormat(locale, { useGrouping: false }).format(
                      Number(date.slice(8)),
                    )}
                  </button>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
